import type { QuestionResolution, TimelineItem } from './types'

const isHandoffTool = (name?: string): boolean =>
  name === 'forward_message' || (name ?? '').startsWith('transfer_')

const isQuestionTool = (name?: string): boolean => name === 'ask_user'

export const isHandoffTimelineItem = (item: TimelineItem): boolean =>
  (item.kind === 'tool_call' || item.kind === 'tool_result') &&
  isHandoffTool(item.name)

export const splitTimeline = (
  items: TimelineItem[]
): { working: TimelineItem[]; final: TimelineItem[] } => {
  let lastToolIdx = -1
  items.forEach((item, i) => {
    if (item.kind === 'tool_call' || item.kind === 'tool_result') {
      if (!isHandoffTool(item.name) && !isQuestionTool(item.name)) {
        lastToolIdx = i
      }
    }
  })

  const working: TimelineItem[] = []
  const final: TimelineItem[] = []

  items.forEach((item, i) => {
    if (item.kind === 'text' && i > lastToolIdx) final.push(item)
    else working.push(item)
  })

  return { working, final }
}

export type TimelineStage = {
  items: TimelineItem[]
  question?: Extract<TimelineItem, { kind: 'question' }>
}

export const splitTimelineStages = (items: TimelineItem[]): TimelineStage[] => {
  const stages: TimelineStage[] = []
  let currentItems: TimelineItem[] = []

  items.forEach((item) => {
    if (item.kind === 'question') {
      stages.push({ items: currentItems, question: item })
      currentItems = []
    } else {
      currentItems.push(item)
    }
  })

  const lastQuestion = stages[stages.length - 1]?.question
  if (
    currentItems.length > 0 ||
    stages.length === 0 ||
    lastQuestion?.status === 'resolved'
  ) {
    stages.push({ items: currentItems })
  }

  return stages
}

export type AnswerSegment = { speaker?: string; text: string }

export const timelineStageAnswerSegments = (
  items: TimelineItem[]
): AnswerSegment[] => {
  const segments: AnswerSegment[] = []

  splitTimeline(items).final.forEach((item) => {
    if (item.kind !== 'text') return
    const speaker = item.speaker || undefined
    const last = segments[segments.length - 1]

    if (last && last.speaker === speaker) last.text += item.text
    else segments.push({ speaker, text: item.text })
  })

  return segments
}

export const timelineStageAnswerText = (items: TimelineItem[]): string =>
  timelineStageAnswerSegments(items)
    .map((segment) => segment.text)
    .join('')

const ANSWERED_QUESTION_OUTPUT =
  /^user answered\s*[—–-]\s*(selected|other)\s*:\s*([\s\S]*\S)\s*$/i

export const parseQuestionResolution = (
  output: unknown
): QuestionResolution | null => {
  if (typeof output !== 'string') return null

  const match = output.match(ANSWERED_QUESTION_OUTPUT)
  if (!match) return null

  const [, kind, value] = match

  return kind.toLowerCase() === 'other'
    ? { selected: [], other: value }
    : { selected: [value] }
}
